import I18n from '@app/adapters/I18n'

export interface PartnerSegmentParams {
  id: string
  name: string
}

// "Partner editing mode": the firm Insights UI was opened from the partner
// section to edit a partner segment's filters. It's detected from the URL
// carrying ms_id + ms_name — the same signal CompaniesOverviewController uses
// ("coming from partner section"). In that mode we only offer firm templates
// that are linked to a partner template, because picking a non-partner template
// can't be converted back to partner level (it would fail conversion).
// Read from the URL, not vue-router: the subnav mini-app has no router.
export function partnerSegmentParams(): PartnerSegmentParams | null {
  const params = new URLSearchParams(window.location.search)
  const id = params.get('ms_id')
  const name = params.get('ms_name')
  if (!id || !name) return null

  return { id, name }
}

export function editingPartnerSegment(): boolean {
  return partnerSegmentParams() !== null
}

// Narrows a dropdown's options for the partner-editing context: when editing a
// partner segment, keeps only items linked to a partner template (every
// dropdown normalises this to a truthy partnerTemplateId). Outside that
// context it's a no-op and returns the list unchanged.
export function filteredTemplates<T>(items: T[] | null | undefined): T[] {
  const list = items ?? []
  if (!editingPartnerSegment()) return list

  return list.filter((item) =>
    Boolean((item as Record<string, unknown>).partnerTemplateId)
  )
}

interface WorkflowOptionSettings {
  partnerWorkflowId?: number | null
  partnerWorkflowName?: string | null
}

interface WorkflowOption {
  value: string | number | null
  label: string
}

// The leading "default workflow" option for a firm-workflow dropdown. A filter
// bound to a partner workflow this firm hasn't imported isn't the default
// workflow (and the binding survives saves), so it's labelled with the partner
// workflow's own name when known (resolved server-side), falling back to a
// generic "not available" when it isn't.
// `value` differs per dropdown (null vs the DEFAULT_WORKFLOW_ID sentinel).
export function defaultWorkflowOption(
  settings: WorkflowOptionSettings | null | undefined,
  value: string | number | null = null
): WorkflowOption {
  let label = I18n.t('firm_workflows.default_label')
  if (settings?.partnerWorkflowId) {
    label =
      settings.partnerWorkflowName ||
      I18n.t('firm_workflows.not_available_label')
  }

  return { value, label }
}
